import { useLayoutEffect, useRef, useState } from "react"
import { Link } from "@inertiajs/react"
import { ChevronDown, Star } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/DropdownMenu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip"
import { type Bookmark, useBookmarks, useCurrentAddress } from "./useBookmarks"

// One line under the top bar, like Chrome's bookmarks bar: each bookmark as a
// chip, oldest first. The chip of the page you are on is tinted. Chips that
// do not fit (a narrow window, the Claude panel open) move into "More" at the
// end, so the bar never grows a second line. Empty, it says how to add one.
export default function BookmarksBar() {
  const bookmarks = useBookmarks()
  const address = useCurrentAddress()
  const rowRef = useRef<HTMLDivElement>(null)
  const [fitCount, setFitCount] = useState(bookmarks.length)

  // Chips wrap inside a one-line box that clips the rest; the ones that
  // wrapped onto the hidden line are the ones that did not fit.
  useLayoutEffect(() => {
    const row = rowRef.current
    if (!row) return
    const measure = () => {
      const chips = [...row.children] as HTMLElement[]
      const firstTop = chips[0]?.offsetTop ?? 0
      const fit = chips.findIndex((chip) => chip.offsetTop > firstTop)
      setFitCount(fit === -1 ? chips.length : fit)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(row)
    return () => observer.disconnect()
  }, [bookmarks])

  const hidden = bookmarks.slice(fitCount)

  return (
    <div className="flex h-8 flex-shrink-0 items-center gap-1 border-b border-border bg-background px-3">
      {bookmarks.length === 0 ? (
        <div className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
          Press the <Star size={12} strokeWidth={2} /> star at the top right to bookmark a page. Your bookmarks show here.
        </div>
      ) : (
        <>
          <div ref={rowRef} className="flex h-6 min-w-0 flex-1 flex-wrap gap-1 overflow-hidden">
            {bookmarks.map((b, i) => (
              <Chip key={b.id} bookmark={b} active={b.url === address} hidden={i >= fitCount} />
            ))}
          </div>
          {hidden.length > 0 && <MoreMenu bookmarks={hidden} address={address} />}
        </>
      )}
    </div>
  )
}

function Chip({ bookmark, active, hidden }: { bookmark: Bookmark; active: boolean; hidden: boolean }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          href={bookmark.url}
          aria-current={active ? "page" : undefined}
          aria-hidden={hidden || undefined}
          tabIndex={hidden ? -1 : undefined}
          className={`flex h-6 max-w-52 items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors ${
            active ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
          }`}
        >
          <Star size={12} strokeWidth={2} className="flex-shrink-0 text-primary" fill="currentColor" />
          <span className="truncate">{bookmark.name}</span>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start">
        <div className="font-medium">{bookmark.name}</div>
        {bookmark.description && <div>{bookmark.description}</div>}
      </TooltipContent>
    </Tooltip>
  )
}

function MoreMenu({ bookmarks, address }: { bookmarks: Bookmark[]; address: string }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 flex-shrink-0 gap-1 px-2 text-xs">
          More <ChevronDown size={12} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {bookmarks.map((b) => (
          <DropdownMenuItem key={b.id} asChild className={b.url === address ? "bg-primary/10" : undefined}>
            <Link href={b.url} className="flex flex-col items-start gap-0">
              <span className={`text-sm font-medium ${b.url === address ? "text-primary" : "text-foreground"}`}>{b.name}</span>
              {b.description && <span className="text-xs text-muted-foreground">{b.description}</span>}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
